import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search,
  Plus,
  RefreshCw,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  Zap,
  ExternalLink,
  Clock,
  Users,
  Droplets,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Eye,
  ArrowRightLeft,
  Shuffle
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

import { getApiUrl, getAuthenticatedAxios } from '../utils/api';
import BlumTokenScanner from '../components/blum/BlumTokenScanner';
import BlumTokenCard from '../components/blum/BlumTokenCard';
import BlumTradeForm from '../components/blum/BlumTradeForm';
import BlumHistory from '../components/blum/BlumHistory';
import BlumRandomBuy from '../components/blum/BlumRandomBuy';
import BlumGroupBuy from '../components/blum/BlumGroupBuy';
import BlumOnChainScanner from '../components/blum/BlumOnChainScanner';
import BlumAutoSnipe from '../components/blum/BlumAutoSnipe';

const API_URL = getApiUrl();

const BlumDex = () => {
  const [activeTab, setActiveTab] = useState('scanner');
  const [tonPrice, setTonPrice] = useState(0);
  const [stats, setStats] = useState(null);
  const [watchedTokens, setWatchedTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);

  // Load initial data
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const api = getAuthenticatedAxios();
      
      // Load TON price, stats, and watched tokens in parallel
      const [tonPriceRes, statsRes, tokensRes] = await Promise.all([
        api.get(`${API_URL}/api/blum/ton-price`).catch(() => ({ data: { price_usd: 0 } })),
        api.get(`${API_URL}/api/blum/stats`).catch(() => ({ data: { success: false } })),
        api.get(`${API_URL}/api/blum/tokens`).catch(() => ({ data: { tokens: [] } }))
      ]);

      if (tonPriceRes.data.success !== false) {
        setTonPrice(tonPriceRes.data.price_usd || 0);
      }
      
      if (statsRes.data.success) {
        setStats(statsRes.data);
      }
      
      if (tokensRes.data.success) {
        setWatchedTokens(tokensRes.data.tokens || []);
      }
    } catch (error) {
      console.error('Error loading BLUM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshTokenPrices = async () => {
    setRefreshing(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/blum/tokens/refresh`);
      
      if (response.data.success) {
        toast.success(`Обновлено ${response.data.updated} токенов`);
        await loadData();
      } else {
        toast.error('Ошибка обновления цен');
      }
    } catch (error) {
      toast.error('Ошибка обновления');
      console.error(error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleTokenAdded = (token) => {
    setWatchedTokens(prev => {
      const exists = prev.find(t => t.master_address === token.token_address);
      if (exists) {
        return prev.map(t => 
          t.master_address === token.token_address 
            ? { ...t, ...token, master_address: token.token_address }
            : t
        );
      }
      return [{ ...token, master_address: token.token_address }, ...prev];
    });
  };

  const handleRemoveToken = async (tokenAddress) => {
    try {
      const api = getAuthenticatedAxios();
      await api.delete(`${API_URL}/api/blum/tokens/${tokenAddress}`);
      setWatchedTokens(prev => prev.filter(t => t.master_address !== tokenAddress));
      toast.success('Токен удален из списка');
    } catch (error) {
      toast.error('Ошибка удаления токена');
      console.error(error);
    }
  };

  const handleSelectToken = (token) => {
    setSelectedToken(token);
    setActiveTab('trade');
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '—';
    if (num >= 1e9) return (num / 1e9).toFixed(decimals) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(decimals) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(decimals) + 'K';
    return num.toFixed(decimals);
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    if (price < 0.00001) return `$${price.toExponential(2)}`;
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
              <Zap className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">BLUM DEX</h1>
              <p className="text-purple-200">BCL Token Trading Platform</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={refreshTokenPrices}
              disabled={refreshing}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Обновить
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-200 text-sm mb-1">
                <DollarSign className="h-4 w-4" />
                TON Price
              </div>
              <div className="text-2xl font-bold text-white">
                ${tonPrice.toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-200 text-sm mb-1">
                <Eye className="h-4 w-4" />
                Отслеживаемые
              </div>
              <div className="text-2xl font-bold text-white">
                {stats?.tokens?.watched_tokens || watchedTokens.length}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-200 text-sm mb-1">
                <ArrowRightLeft className="h-4 w-4" />
                Операций
              </div>
              <div className="text-2xl font-bold text-white">
                {stats?.operations?.total_operations || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-200 text-sm mb-1">
                <TrendingUp className="h-4 w-4" />
                Покупок
              </div>
              <div className="text-2xl font-bold text-green-400">
                {stats?.operations?.total_buys || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-200 text-sm mb-1">
                <TrendingDown className="h-4 w-4" />
                Продаж
              </div>
              <div className="text-2xl font-bold text-red-400">
                {stats?.operations?.total_sells || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/10 border border-white/20 p-1">
            <TabsTrigger
              value="scanner"
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-purple-200"
            >
              <Search className="h-4 w-4 mr-2" />
              Token Scanner
            </TabsTrigger>
            <TabsTrigger
              value="onchain"
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-purple-200"
            >
              <Zap className="h-4 w-4 mr-2" />
              On-Chain
            </TabsTrigger>
            <TabsTrigger
              value="watchlist"
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-purple-200"
            >
              <Eye className="h-4 w-4 mr-2" />
              Watchlist
            </TabsTrigger>
            <TabsTrigger 
              value="trade" 
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-purple-200"
            >
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Trade
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-purple-200"
            >
              <Clock className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger 
              value="random-buy" 
              className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-orange-200"
            >
              <Shuffle className="h-4 w-4 mr-2" />
              Random Buy
            </TabsTrigger>
            <TabsTrigger 
              value="group-buy" 
              className="data-[state=active]:bg-green-500 data-[state=active]:text-white text-green-200"
            >
              <Users className="h-4 w-4 mr-2" />
              Group Buy
            </TabsTrigger>
            <TabsTrigger 
              value="auto-snipe" 
              className="data-[state=active]:bg-red-500 data-[state=active]:text-white text-red-200"
            >
              <Zap className="h-4 w-4 mr-2" />
              Auto-Snipe
            </TabsTrigger>
          </TabsList>

          {/* Token Scanner Tab */}
          <TabsContent value="scanner" className="mt-6">
            <BlumTokenScanner
              onTokenAdded={handleTokenAdded}
              tonPrice={tonPrice}
            />
          </TabsContent>

          {/* On-Chain Scanner Tab (Phase 11) */}
          <TabsContent value="onchain" className="mt-6">
            <BlumOnChainScanner />
          </TabsContent>

          {/* Watchlist Tab */}
          <TabsContent value="watchlist" className="mt-6">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Отслеживаемые токены
                </CardTitle>
                <CardDescription className="text-purple-200">
                  Список токенов BLUM для мониторинга и торговли
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                  </div>
                ) : watchedTokens.length === 0 ? (
                  <div className="text-center py-12 text-purple-200">
                    <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Нет отслеживаемых токенов</p>
                    <p className="text-sm opacity-75">Добавьте токен через Token Scanner</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {watchedTokens.map((token) => (
                      <BlumTokenCard 
                        key={token.master_address}
                        token={token}
                        tonPrice={tonPrice}
                        onRemove={() => handleRemoveToken(token.master_address)}
                        onTrade={() => handleSelectToken(token)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trade Tab */}
          <TabsContent value="trade" className="mt-6">
            <BlumTradeForm 
              selectedToken={selectedToken}
              watchedTokens={watchedTokens}
              tonPrice={tonPrice}
              onSelectToken={setSelectedToken}
            />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            <BlumHistory />
          </TabsContent>

          {/* Random Buy Tab */}
          <TabsContent value="random-buy" className="mt-6">
            <BlumRandomBuy 
              tokens={watchedTokens}
              onRefreshTokens={loadData}
            />
          </TabsContent>

          {/* Group Buy Tab */}
          <TabsContent value="group-buy" className="mt-6">
            <BlumGroupBuy 
              tokens={watchedTokens}
              selectedToken={selectedToken}
              onTokenSelect={setSelectedToken}
            />
          </TabsContent>

          <TabsContent value="auto-snipe" className="mt-6">
            <BlumAutoSnipe />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BlumDex;


